/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
import { clsx } from '@accelint/design-foundation/lib/utils';
import styles from './styles.module.css';
import type { TextProps } from 'react-aria-components/Text';

/**
 * OptionsItemContent - Container for label and description within an item
 *
 * Use when an item needs both a label and description.
 *
 * @example
 * ```tsx
 * <OptionsItem>
 *   <OptionsItemContent>
 *     <OptionsItemLabel>Option Name</OptionsItemLabel>
 *     <OptionsItemDescription>Description text</OptionsItemDescription>
 *   </OptionsItemContent>
 * </OptionsItem>
 * ```
 *
 * @param props - TextProps from react-aria-components.
 * @param props.className - Optional CSS class name.
 * @returns The rendered OptionsItemContent component.
 */
export function OptionsItemContent({ className, ...rest }: TextProps) {
  return <div {...rest} className={clsx(styles.content, className)} />;
}
